import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './PatternsRadar.css';
import patternsRadarService from '../../services/patternsRadarService';
import { formatCurrency } from './patternsRadarUtils';
import ScreenerStudio from './components/ScreenerStudio';
import StrategyBacktest from './components/StrategyBacktest';
import MarketExplorer from './components/MarketExplorer';
import SavedScansAlerts from './components/SavedScansAlerts';
import QuotaSettingsModal from './components/QuotaSettingsModal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faSlidersH,
  faFilter,
  faHistory,
  faBuilding,
  faBookmark,
  faServer,
  faSpinner,
  faChartLine,
} from '@fortawesome/free-solid-svg-icons';

const PatternsRadar = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('screener'); // 'screener', 'backtest', 'market', 'saved'
  const [backtestSeedQuery, setBacktestSeedQuery] = useState('');
  const [screenerSeedQuery, setScreenerSeedQuery] = useState('');

  // Top Bar Meta & Health state
  const [meta, setMeta] = useState(null);
  const [healthOk, setHealthOk] = useState(true);
  const [account, setAccount] = useState(null);

  // Global Omni-Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  // Active Symbol 360 Modal state
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  // Quota & Settings Modal
  const [showSettings, setShowSettings] = useState(false);

  const loadHeaderData = async () => {
    try {
      const [metaData, healthData, accData] = await Promise.all([
        patternsRadarService.getMeta().catch(() => null),
        patternsRadarService.checkHealth().catch(() => ({ status: 'error' })),
        patternsRadarService.whoAmI().catch(() => null),
      ]);

      setMeta(metaData);
      setHealthOk(healthData?.status === 'ok');
      setAccount(accData);
    } catch (e) {
      console.error('Failed to load PatternsRadar metadata:', e);
    }
  };

  useEffect(() => {
    loadHeaderData();
  }, []);

  // Omni-search debouncer
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await patternsRadarService.searchSymbols(searchQuery.trim(), 8);
        setSearchResults(res.hits || []);
        setShowDropdown(true);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 280);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Click outside search dropdown closer
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectSymbol = (sym) => {
    setShowDropdown(false);
    setSearchQuery('');
    navigate(`/research/patterns-radar/symbol/${sym}`);
  };

  const handleSendToBacktest = (query) => {
    setBacktestSeedQuery(query);
    setActiveTab('backtest');
  };

  return (
    <div className="patterns-radar-container w-100 mt-5 pt-2">
      {/* Breadcrumb - Standard StreetGains CRM format */}
        <div className="row pb-2">
          <div className="col-md-6">
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="/dashboard">
                  <i className="ti ti-home"></i> Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <span className="text-muted">Research</span>
              </li>
              <li className="breadcrumb-item active">Street Analysis</li>
            </ul>
          </div>
          <div className="col-md-6 text-end">
            <button
              className="btn btn-default btn-sm pull-right"
              onClick={() => setShowSettings(true)}
              title="API Keys & Plan Quotas"
            >
              <FontAwesomeIcon icon={faSlidersH} className="me-1 text-primary" />
              Settings & Quota
            </button>
          </div>
        </div>

        {/* Global Market Intelligence & Meta Bar */}
        <div className="pr-header-card">
          {/* Row 1: All Status & Coverage Badges in one row */}
          <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
            <span
              className={`pr-badge ${
                healthOk ? 'pr-badge-online' : 'pr-badge-offline'
              }`}
            >
              <FontAwesomeIcon icon={faServer} />
              {healthOk ? 'NSE Engine: Live' : 'API Offline'}
            </span>

            {meta && (
              <>
                <span className="badge bg-light text-dark border">
                  Coverage: {meta.symbol_count} Symbols
                </span>
                <span className="badge bg-light text-dark border">
                  {(meta.bar_rows / 1000000).toFixed(1)}M Historical Bars
                </span>
                {meta.data_stale && (
                  <span className="badge bg-warning text-dark">
                    Session Pending
                  </span>
                )}
              </>
            )}
          </div>

          {/* Row 2: Omni-Symbol Search Bar */}
          <div className="pr-search-wrapper w-100" ref={searchRef}>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white border-end-0">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Quick Symbol Search (e.g. RELIANCE, TCS, INFY)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (searchResults.length > 0) setShowDropdown(true);
                }}
              />
              {searchLoading && (
                <span className="input-group-text bg-white border-start-0">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="text-primary"
                  />
                </span>
              )}
            </div>

            {/* Search Auto-Suggest Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div className="pr-search-dropdown">
                {searchResults.map((hit) => (
                  <div
                    key={hit.symbol}
                    className="pr-search-item"
                    onClick={() => handleSelectSymbol(hit.symbol)}
                  >
                    <div>
                      <strong className="text-primary me-2">
                        {hit.symbol}
                      </strong>
                      <span className="text-muted">{hit.name}</span>
                    </div>
                    <div className="text-end">
                      <span className="badge bg-light text-dark me-2">
                        {hit.kind?.toUpperCase()}
                      </span>
                      <span className="font-weight-bold">
                        {formatCurrency(hit.last_close)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation - Matching StreetGains CRM styles */}
        <div className="pr-nav-tabs">
          <button
            className={`nav-link ${activeTab === 'screener' ? 'active' : ''}`}
            onClick={() => setActiveTab('screener')}
          >
            <FontAwesomeIcon icon={faFilter} />
            1. Screener Studio
          </button>
          <button
            className={`nav-link ${activeTab === 'backtest' ? 'active' : ''}`}
            onClick={() => setActiveTab('backtest')}
          >
            <FontAwesomeIcon icon={faHistory} />
            2. Strategy Backtester
          </button>
          <button
            className={`nav-link ${activeTab === 'market' ? 'active' : ''}`}
            onClick={() => setActiveTab('market')}
          >
            <FontAwesomeIcon icon={faBuilding} />
            3. Market Explorer
          </button>
          <button
            className={`nav-link ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            <FontAwesomeIcon icon={faBookmark} />
            4. Saved Scans & Alerts
          </button>
        </div>

        {/* Tab Content Display */}
        {activeTab === 'screener' && (
          <ScreenerStudio
            initialQuery={screenerSeedQuery}
            onOpenSymbol={(sym) =>
              navigate(`/research/patterns-radar/symbol/${sym}`)
            }
            onSendToBacktest={handleSendToBacktest}
          />
        )}

        {activeTab === 'backtest' && (
          <StrategyBacktest initialQuery={backtestSeedQuery} />
        )}

        {activeTab === 'market' && (
          <MarketExplorer
            onOpenSymbol={(sym) =>
              navigate(`/research/patterns-radar/symbol/${sym}`)
            }
          />
        )}

        {activeTab === 'saved' && (
          <SavedScansAlerts
            onRunScan={(src) => {
              setScreenerSeedQuery(src);
              setActiveTab('screener');
            }}
          />
        )}

      {showSettings && (
        <QuotaSettingsModal
          onClose={() => setShowSettings(false)}
          onKeyUpdated={loadHeaderData}
        />
      )}
    </div>
  );
};

export default PatternsRadar;
